// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * AssignmentHistoryDialog — who changed a patient's assignees, when, before and
 * after (vic/vetpo#70). Master-only, opened from the PetShow header beside
 * Assign vet.
 *
 * GET /api/pets/:id/assignment-history → { vets: [{id,name,email,isMaster}],
 * history: [{id, changedAt, actorId, before, after}] } newest first. `vets`
 * names only the caller's own team; an id outside it (another clinic's vet on
 * a shared patient) stays a bare `#id`.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'
import { useDateFormatter } from '../utils/localeDate'

interface HistoryRow {
  id: number
  changedAt: string
  actorId: number | null
  before: number[]
  after: number[]
}
interface HistoryVet {
  id: number
  name?: string | null
  email?: string | null
}

interface Props {
  open: boolean
  petId: number
  onClose: () => void
}

const HISTORY_LIMIT = 50

const AssignmentHistoryDialog: React.FC<Props> = ({ open, petId, onClose }) => {
  const translate = useTranslate()
  const formatDate = useDateFormatter()
  const [rows, setRows] = useState<HistoryRow[]>([])
  const [vets, setVets] = useState<HistoryVet[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    if (!open) return
    const ctrl = new AbortController()
    setState('loading')
    ;(async () => {
      try {
        const res = await deadlineFetch(
          `/api/pets/${petId}/assignment-history?limit=${HISTORY_LIMIT}`,
          { headers: authHeaders(), signal: ctrl.signal },
        )
        if (!res.ok) throw new Error(String(res.status))
        const data = await res.json()
        if (ctrl.signal.aborted) return
        setRows(Array.isArray(data?.history) ? data.history : [])
        setVets(Array.isArray(data?.vets) ? data.vets : [])
        setState('ready')
      } catch {
        if (!ctrl.signal.aborted) setState('error')
      }
    })()
    return () => ctrl.abort()
  }, [open, petId])

  const nameOf = (id: number | null): string => {
    if (id == null) return translate('pos.clinic_team.history_system')
    const v = vets.find((x) => Number(x.id) === Number(id))
    return v?.name || v?.email || `#${id}`
  }
  const list = (ids: number[]): string =>
    ids?.length ? ids.map(nameOf).join(', ') : translate('pos.clinic_team.unassigned_short')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('pos.clinic_team.history_title')}</DialogTitle>
      <DialogContent dividers>
        {state === 'loading' && <CircularProgress size={24} />}
        {state === 'error' && (
          <Typography color="error" variant="body2" data-testid="assignment-history-error">
            {translate('pos.clinic_team.history_error')}
          </Typography>
        )}
        {state === 'ready' && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate('pos.clinic_team.history_empty')}
          </Typography>
        )}
        {state === 'ready' && rows.length > 0 && (
          <Table size="small" data-testid="assignment-history-table">
            <TableHead>
              <TableRow>
                <TableCell>{translate('pos.clinic_team.history_when')}</TableCell>
                <TableCell>{translate('pos.clinic_team.history_by')}</TableCell>
                <TableCell>{translate('pos.clinic_team.history_before')}</TableCell>
                <TableCell>{translate('pos.clinic_team.history_after')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{formatDate(new Date(r.changedAt), 'dateTime')}</TableCell>
                  <TableCell>{nameOf(r.actorId)}</TableCell>
                  <TableCell>{list(r.before)}</TableCell>
                  <TableCell>{list(r.after)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('ra.action.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignmentHistoryDialog
